import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { selectCurrentUser } from 'store/users/users.selectors'
import { selectSelectLists } from 'store/selectLists/selectLists.selectors'
import { selectCurrentTime } from 'store/utils/utils.selectors'

import { UserRole, Feature } from 'types/user'

import { getPaymentBlockingReason } from 'utils/payment-limit'
import { OrderFields } from 'types/order'

import OrderForm from 'components/organisms/OrderForm'
import BlockingWarning from 'components/organisms/CreateOrderBody/BlockingWarning'
import OrderTypes from 'components/molecules/OrderFormSpace/spaces/Order/OrderTypes'
import { intendedOrderTypeStorageKey } from 'routes/url-constants'
import getInitialValues from './utils/getInitialValues'

export const CreateOrderBody: React.FC = () => {
  const location = useLocation()
  const currentUser = useSelector(selectCurrentUser)
  const selectLists = useSelector(selectSelectLists)
  const currentTime = useSelector(selectCurrentTime)

  const orderTypeFromQuery = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    const fromUrl = searchParams.get(OrderFields.ORDER_TYPE)
    if (fromUrl) return fromUrl

    // Fallback: OAuth/v3 can strip query params during redirects, but we preserve intended orderType in sessionStorage
    // (see `private-route` + `intendedOrderTypeStorageKey`).
    try {
      const fromSession = sessionStorage.getItem(intendedOrderTypeStorageKey)
      return fromSession && fromSession.trim() ? fromSession.trim() : null
    } catch {
      return null
    }
  }, [location.search])

  const availableOrderTypes = useMemo(() => {
    if (currentUser?.role === UserRole.CUSTOMER) {
      return OrderTypes.getForExternalUser(currentUser?.features?.[Feature.AVAILABLE_ORDER_TYPES] || null, selectLists?.orderType || [])
    }

    return OrderTypes.getForInternalUser(selectLists?.orderType || [])
  }, [currentUser, selectLists])

  const initialValues = useMemo(() => {
    const values = getInitialValues(currentUser, selectLists, currentTime)
    const hasOrderTypeInQuery = availableOrderTypes.some((item) => String(item.id) === String(orderTypeFromQuery))

    if (hasOrderTypeInQuery && orderTypeFromQuery) {
      values[OrderFields.ORDER_TYPE] = String(orderTypeFromQuery)
    }

    return values
  }, [availableOrderTypes, currentTime, currentUser, orderTypeFromQuery, selectLists])

  useEffect(() => {
    if (!orderTypeFromQuery) return
    const hasOrderTypeInQuery = availableOrderTypes.some((item) => String(item.id) === String(orderTypeFromQuery))
    if (!hasOrderTypeInQuery) return

    try {
      // Clear fallback storage once a valid type is applied/confirmed to avoid sticky behavior
      const searchParams = new URLSearchParams(location.search)
      const inUrl = searchParams.get(OrderFields.ORDER_TYPE)
      if (inUrl && String(inUrl) === String(orderTypeFromQuery)) {
        sessionStorage.removeItem(intendedOrderTypeStorageKey)
        return
      }
      if (!inUrl) {
        sessionStorage.removeItem(intendedOrderTypeStorageKey)
      }
    } catch {
      // ignore
    }
  }, [availableOrderTypes, location.search, orderTypeFromQuery])

  const currentUserIsCustomer = currentUser!.role === UserRole.CUSTOMER
  const blockingReason = getPaymentBlockingReason(currentUser?.features)
  if (currentUserIsCustomer && blockingReason) {
    return <BlockingWarning reason={blockingReason} />
  }

  return <OrderForm isCreate initialValues={initialValues} />
}

export default CreateOrderBody
